import type { TRPCClientErrorBase } from '@trpc/client';
import type { TRPCDefaultErrorShape } from '@trpc/server';

import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { UseTRPCQueryResult } from '@trpc/react-query/shared';
import { addDays } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { FilterParameters, ResourceCalendarFilters } from '../../../components/general';
import { VeranstaltungPropertiesChanger } from '../../../components/general/VivaRessourcenCalendars/VeranstaltungRessourcenCalendar/helperComponents';
import {
  CheckDurchgangAvailabilityInput,
  GetBookableExpertenByThemaOutput,
  type Terminart,
  ThemaFromThemaRouter,
} from '../../../dtos.ts';
import { trpc } from '../../../trpc.ts';
import { isOnlineAndOrPraesenz, onlineAndOrPraesenz } from '../../../utils/isOnline.ts';
import { content } from '../CreateDurchgang.content.ts';
import { SaveButtonState } from '../DurchgangPlanung.types.ts';
import { allRequiredFieldsDefined } from './CreateDurchgangInput.utils.ts';
import { CreateDurchgangModul } from './CreateDurchgangModul.tsx';
import { type PlanningData } from './planningData.types.ts';

type CreateDurchgangInputProps = Readonly<{
  thema: Pick<
    ThemaFromThemaRouter,
    'akaProduktId' | 'module' | 'planungsinformation' | 'isOnline' | 'planungsformat' | 'manuellerAusschreibungsprozess'
  >;
  expertePool: UseTRPCQueryResult<GetBookableExpertenByThemaOutput, TRPCClientErrorBase<TRPCDefaultErrorShape>>;
  useExpertenForAllDays: boolean;
  setUseExpertenForAllDays: React.Dispatch<React.SetStateAction<boolean>>;
  checkDurchgangAvailabilityQuery: ReturnType<typeof trpc.durchgang.checkDurchgangAvailability.useMutation>;
  planningData: PlanningData;
  setPlanningData: Dispatch<SetStateAction<PlanningData>>;
  setSaveButtonState: Dispatch<SetStateAction<SaveButtonState>>;
  filterParams: FilterParameters;
  setFilterParams: React.Dispatch<React.SetStateAction<FilterParameters>>;
}>;

export const CreateDurchgangInput = ({
  thema,
  expertePool,
  useExpertenForAllDays,
  setUseExpertenForAllDays,
  checkDurchgangAvailabilityQuery,
  planningData,
  setPlanningData,
  setSaveButtonState,
  filterParams,
  setFilterParams,
}: CreateDurchgangInputProps) => {
  const handleTerminartChange = (terminart: NonNullable<Terminart>) => {
    setPlanningData((prev) => ({
      ...prev,
      terminart,
    }));
    setSaveButtonState((prev) =>
      // This ternary is needed since saveInputData itself is optional and undefined spreads to {}. Without it there's a type error
      prev.saveInputData ? { ...prev, saveInputData: { ...prev.saveInputData, terminart } } : prev,
    );
  };

  const getAdjustedZeitraumDates = (
    date: Date | null,
    zeitraum: { start: Date | null; end: Date | null },
    index: number,
  ) => {
    if (!date || !zeitraum.start || !zeitraum.end) {
      throw new Error('modul.datum or modul.termine[?].uhrzeiten is null');
    }

    const start = addDays(new Date(date), index);
    start.setHours(zeitraum.start.getHours(), zeitraum.start.getMinutes(), 0, 0);

    const end = addDays(new Date(date), index);
    end.setHours(zeitraum.end.getHours(), zeitraum.end.getMinutes(), 0, 0);

    return { start, end };
  };

  const handleCheckAvailability = async () => {
    const inputData: CheckDurchgangAvailabilityInput = {
      akaProduktId: thema.akaProduktId,
      terminart: planningData.terminart,
      module: planningData.module.map((modul) => ({
        modulnummer: modul.modulnummer,
        gruppenraumInfo: modul.gruppenraumInfo,
        tage: modul.termine.map((termin, index) => ({
          zeitraum: getAdjustedZeitraumDates(modul.datum, termin.uhrzeiten, index),
          expertenSapIds: termin.expertenSapIds,
        })),
      })),
    };
    await checkDurchgangAvailabilityQuery.mutateAsync(inputData);
    setFormDirty(false);
  };

  const [formDirty, setFormDirty] = useState(false);

  const resetDurchgangAvailability = useCallback(() => {
    setSaveButtonState({ disabled: true, tooltip: '', saveInputData: undefined });
    checkDurchgangAvailabilityQuery.reset();
    setFormDirty(true);
  }, [checkDurchgangAvailabilityQuery, setSaveButtonState, setFormDirty]);

  return (
    <Stack spacing={0.5}>
      <VeranstaltungPropertiesChanger
        terminart={planningData.terminart}
        onTerminartChange={handleTerminartChange}
        hideWithRessourcen
        manuellerAusschreibungsprozess={thema.manuellerAusschreibungsprozess}
      />
      <Paper sx={{ p: 3 }}>
        <Stack direction="column" spacing={2}>
          {isOnlineAndOrPraesenz(thema.isOnline, thema.planungsformat, thema.module) !== onlineAndOrPraesenz.ONLINE && (
            <>
              <ResourceCalendarFilters
                filterParameters={filterParams}
                startYear={thema.planungsinformation?.planungszeitraum?.start.getFullYear() ?? new Date().getFullYear()}
                onFilterChange={(filter) => {
                  setFilterParams(filter);
                }}
                padding={0}
                hiddenFilters={['freieTage']}
              />

              <Divider />
            </>
          )}

          <Stack spacing={2}>
            {thema.module.map((modul, modulIndex) => {
              const previousModulDatum = modulIndex > 0 ? planningData.module[modulIndex - 1].datum : null;
              const currentReferenceDate =
                previousModulDatum ?? thema.planungsinformation?.planungszeitraum?.start ?? null;

              return (
                <CreateDurchgangModul
                  key={`modul${modul.modulnummer}`}
                  modul={modul}
                  modulIndex={modulIndex}
                  planningData={planningData}
                  resetDurchgangAvailability={resetDurchgangAvailability}
                  setPlanningData={setPlanningData}
                  setSaveButtonState={setSaveButtonState}
                  expertePool={expertePool.data}
                  useExpertenForAllDays={useExpertenForAllDays}
                  setUseExpertenForAllDays={setUseExpertenForAllDays}
                  referenceDate={currentReferenceDate}
                />
              );
            })}

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="labelSmLight" color="textTertiary">
                {content.requiredField}
              </Typography>

              <Button
                size="small"
                variant="contained"
                disabled={!allRequiredFieldsDefined(planningData) || !formDirty}
                loading={checkDurchgangAvailabilityQuery.isPending}
                onClick={() => void handleCheckAvailability()}
              >
                {
                  <Box sx={{ opacity: checkDurchgangAvailabilityQuery.isPending ? 0 : 1 }}>
                    {content.checkAvailability}
                  </Box>
                }
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};
